import { motion, useReducedMotion } from "motion/react"
import { useState } from "react"

export type EstadoEntrar = "reposo" | "enviando" | "ok"

/**
 * El botón de la pantalla de acceso, con sus tres estados a la vista:
 *
 * - **Al pulsarlo**, un destello dorado lo cruza UNA vez. Dice «recibido» en el
 *   mismo instante del clic, antes de que el servidor conteste.
 * - **Mientras entra**, tres puntos que suben y bajan. Sin ellos, los cientos de ms
 *   que tarda el servidor se leen como un botón que no hizo nada, y se vuelve a
 *   pulsar.
 * - **Al entrar**, una palomita que se dibuja. Es lo último que se ve antes de que
 *   la lámina viaje al menú: confirma que lo que viene es el sistema y no otro
 *   error.
 *
 * ⚠ **El nombre accesible cambia con el estado** («Entrar», «Entrando…»,
 * «Bienvenido»), y los adornos van `aria-hidden`: un lector anuncia la palabra, no
 * tres puntos.
 */
export function BotonEntrar({ estado }: { estado: EstadoEntrar }) {
  const quieto = useReducedMotion()
  // Cada pulsación vuelve a lanzar el destello: la clave reinicia la animación.
  const [pasada, setPasada] = useState(0)
  const ocupado = estado !== "reposo"

  return (
    <motion.button
      type="submit"
      disabled={ocupado}
      onPointerDown={() => setPasada((n) => n + 1)}
      whileHover={ocupado ? undefined : { y: -1 }}
      whileTap={ocupado ? undefined : { scale: 0.985 }}
      transition={{ type: "spring", stiffness: 400, damping: 28 }}
      className="boton-cristal foco-cristal relative mt-1 overflow-hidden px-4 py-3 text-sm font-bold disabled:cursor-progress"
    >
      {!quieto && pasada > 0 && (
        <motion.span
          key={pasada}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[linear-gradient(100deg,transparent_30%,rgb(var(--acceso-oro-claro)/0.35)_50%,transparent_70%)]"
          initial={{ x: "-120%" }}
          animate={{ x: "120%" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        />
      )}
      <span className="relative inline-flex items-center justify-center gap-2">
        {estado === "reposo" && "Entrar"}
        {estado === "enviando" && (
          <>
            Entrando…
            <span aria-hidden="true" className="inline-flex gap-1">
              {[0, 1, 2].map((i) => (
                <motion.i
                  key={i}
                  className="block size-[5px] rounded-full bg-current"
                  animate={quieto ? { opacity: 0.7 } : { opacity: [0.25, 1, 0.25], y: [0, -2, 0] }}
                  transition={{
                    duration: 0.9,
                    repeat: Number.POSITIVE_INFINITY,
                    delay: i * 0.15,
                    ease: "easeInOut",
                  }}
                />
              ))}
            </span>
          </>
        )}
        {estado === "ok" && (
          <>
            Bienvenido
            <svg viewBox="0 0 24 24" className="size-[18px]" fill="none" aria-hidden="true">
              <motion.path
                d="M5 12.5l4.5 4.5L19 7.5"
                stroke="currentColor"
                strokeWidth={3}
                strokeLinecap="round"
                strokeLinejoin="round"
                initial={{ pathLength: quieto ? 1 : 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.26, ease: [0.16, 1, 0.3, 1] }}
              />
            </svg>
          </>
        )}
      </span>
    </motion.button>
  )
}
